import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import type { PineParseSessionState } from "@/components/Strategies/StrategyPineEditor";
import { StrategyPineEditor } from "@/components/Strategies/StrategyPineEditor";
import { StrategyPineHelpPanel } from "@/components/Strategies/StrategyPineHelpPanel";
import { TesseraFriendlyStructurePanel } from "@/components/Strategies/TesseraFriendlyStructurePanel";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "@/components/Strategies/StrategyPineSectionCard";
import { createReportStrategy } from "../../../services/reportsApi";
import type { ReportStrategyResponse } from "../../../types/reports";
import type { WorkspaceCreateContext } from "../../../types/workspace";
import { canSavePineAfterParse } from "../../../utils/pineIrUi";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { BrokerAccountsMultiSelect } from "@/components/Broker/BrokerAccountsMultiSelect";
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount";
import type { BrokerAccountRef } from "@/utils/brokerAccountRef";
import { resolveEntityBrokerAccounts } from "@/utils/brokerAccountRef";
import { filterConnectedTradeAccountsForUi } from "@/utils/tradeAccountSource";
import {
  GenerateReportWizardStepper,
  GenerateReportWizardStepperContent,
} from "../Reports/GenerateReportWizardStepper";
import { WorkspaceAttachExistingPicker } from "../Workspace/WorkspaceAttachExistingDialog";
import {
  useWorkspaceWizardFooterSlot,
  WORKSPACE_WIZARD_DIALOG_SIZE_CLASS,
  WorkspaceWizardDialog,
} from "../Workspace/WorkspaceWizardDialog";

export type CreateStrategyExistingAttach = {
  brokerAccountCode: string;
  excludeEntityIds: ReadonlySet<string> | string[];
  onAttach: (entityId: string) => void | Promise<void>;
  /** Empty copy when every listed strategy is already linked to this account. */
  strategyAlreadyTakenMessage?: string;
};

export type CreateStrategyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (strategy: ReportStrategyResponse) => void;
  workspaceContext?: WorkspaceCreateContext;
  /** Account scope stamped onto the new strategy (required for dashboard filtering). */
  brokerAccountCode?: string | null;
  brokerName?: string | null;
  /** When set, the plus dialog also offers linking an existing strategy. */
  existingAttach?: CreateStrategyExistingAttach;
};

type CreateStrategyDialogMode = "create" | "existing";

const CREATE_STRATEGY_STEPS = [
  {
    key: "details",
    label: "Strategy details",
    description: "Name your strategy and add an optional description.",
  },
  {
    key: "pinescript",
    label: "Pinescript",
    description:
      "Optionally paste master Pinescript. You can skip this and add it later from the strategy page.",
  },
] as const;

type CreateStrategyStepKey = (typeof CREATE_STRATEGY_STEPS)[number]["key"];

function CreateStrategyFieldBlock({
  label,
  description,
  required,
  htmlFor,
  children,
}: {
  label: string;
  description: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Field
      className="w-full min-w-0"
      data-create-strategy-dialog-fields=""
    >
      <FieldLabel htmlFor={htmlFor} className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "min-h-5 leading-5")}>
        {label}
        {required ? (
          <span className="ml-0.5 text-[#E00101]" aria-hidden>
            *
          </span>
        ) : null}
      </FieldLabel>
      <div className="min-h-9 w-full min-w-0 flex flex-col items-stretch gap-1">{children}</div>
      <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{description}</FieldDescription>
    </Field>
  );
}

export function CreateStrategyDialog({
  open,
  onOpenChange,
  onCreated,
  workspaceContext,
  brokerAccountCode,
  brokerName,
  existingAttach,
}: CreateStrategyDialogProps): React.ReactElement {
  const { accounts: tradeAccounts } = useActiveBrokerAccount();
  const accountOptions = React.useMemo<BrokerAccountRef[]>(() => {
    return filterConnectedTradeAccountsForUi(tradeAccounts).map((account) => ({
      brokerName: String(account.brokerName || "").trim().toLowerCase(),
      brokerAccountCode: String(account.accountCode || "").trim(),
    })).filter((ref) => ref.brokerName && ref.brokerAccountCode);
  }, [tradeAccounts]);

  const defaultAccounts = React.useMemo(() => {
    return resolveEntityBrokerAccounts({
      brokerName,
      brokerAccountCode,
    });
  }, [brokerAccountCode, brokerName]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pinescript, setPinescript] = useState("");
  const [creating, setCreating] = useState(false);
  const [pineSession, setPineSession] = useState<PineParseSessionState | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [selectedAccounts, setSelectedAccounts] = useState<BrokerAccountRef[]>([]);
  const [mode, setMode] = useState<CreateStrategyDialogMode>("create");

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
    setPinescript("");
    setPineSession(null);
    setCreating(false);
    setActiveStepIndex(0);
    setFormKey((key) => key + 1);
    setSelectedAccounts(defaultAccounts);
    setMode("create");
  }, [defaultAccounts]);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    setSelectedAccounts((prev) => (prev.length > 0 ? prev : defaultAccounts));
  }, [open, resetForm, defaultAccounts]);

  const pineReady = useMemo(
    () => !pinescript.trim() || canSavePineAfterParse(pinescript, pineSession?.parsedScript ?? null),
    [pineSession?.parsedScript, pinescript]
  );

  const activeStepKey: CreateStrategyStepKey =
    CREATE_STRATEGY_STEPS[activeStepIndex]?.key ?? "details";
  const lastStepIndex = CREATE_STRATEGY_STEPS.length - 1;

  const canAdvanceDetails = Boolean(name.trim()) && selectedAccounts.length > 0;
  /** Name + accounts required; description and Pinescript are both optional. */
  const canCreate =
    Boolean(name.trim()) && selectedAccounts.length > 0 && pineReady;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
    },
    [onOpenChange]
  );

  const goBack = useCallback(() => {
    setActiveStepIndex((index) => Math.max(0, index - 1));
  }, []);

  const goNext = useCallback(() => {
    if (activeStepKey === "details" && !name.trim()) {
      toast.warning("Name required", { description: "Enter a strategy name." });
      return;
    }
    setActiveStepIndex((index) => Math.min(lastStepIndex, index + 1));
  }, [activeStepKey, lastStepIndex, name]);

  const handleCreate = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedPinescript = pinescript.trim();

    if (!trimmedName) {
      toast.warning("Name required", { description: "Enter a strategy name." });
      return;
    }
    if (
      trimmedPinescript &&
      !canSavePineAfterParse(trimmedPinescript, pineSession?.parsedScript ?? null)
    ) {
      toast.warning("Parse required", {
        description: "Click Parse on your Pinescript before creating the strategy.",
      });
      return;
    }

    const accounts = selectedAccounts.map((account) => ({
      brokerName: account.brokerName.trim().toLowerCase(),
      brokerAccountCode: account.brokerAccountCode.trim(),
    }));
    if (accounts.length === 0) {
      toast.warning("Account required", {
        description: "Select at least one broker account for this strategy.",
      });
      return;
    }
    const primary = accounts[0]!;

    // BE still requires description and/or pinescript; treat description as optional in the UI
    // by sending "" when Pine is present, or the name as a stand-in when both are blank.
    const descriptionForApi =
      trimmedDescription || (trimmedPinescript ? "" : trimmedName);

    setCreating(true);
    try {
      const created = await createReportStrategy({
        name: trimmedName,
        description: descriptionForApi,
        ...(trimmedPinescript ? { pinescript: trimmedPinescript } : {}),
        accounts,
        broker_account_code: primary.brokerAccountCode,
        broker_name: primary.brokerName,
        ...(workspaceContext
          ? {
              workspaceId: workspaceContext.workspaceId,
              parentNodeId: workspaceContext.parentNodeId,
              position: workspaceContext.position,
            }
          : {}),
      });
      toast.success("Strategy created");
      resetForm();
      onOpenChange(false);
      onCreated(created);
    } catch (error) {
      presentMutationError(error, "Create failed");
    } finally {
      setCreating(false);
    }
  }, [
    description,
    name,
    onCreated,
    onOpenChange,
    pineSession?.parsedScript,
    pinescript,
    resetForm,
    selectedAccounts,
    workspaceContext,
  ]);

  const stepBody =
    activeStepKey === "details" ? (
      <div className="flex w-full min-w-0 flex-col gap-5">
        <CreateStrategyFieldBlock
          label="Accounts"
          required
          description="Strategies can belong to one or more broker accounts. Defaults to the current workspace account."
        >
          <BrokerAccountsMultiSelect
            options={accountOptions}
            selected={selectedAccounts}
            onChange={setSelectedAccounts}
            emptyLabel="Choose accounts…"
          />
        </CreateStrategyFieldBlock>

        <CreateStrategyFieldBlock
          label="Name"
          required
          htmlFor="create-strategy-name"
          description="A short label for this strategy in the hub and on the canvas."
        >
          <Input
            id="create-strategy-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. NY Reversal Setup"
            className="font-[family-name:var(--font-primary)]"
          />
        </CreateStrategyFieldBlock>

        <CreateStrategyFieldBlock
          label="Description"
          htmlFor="create-strategy-description"
          description="Optional — leave blank if you prefer. You can add details later."
        >
          <Textarea
            id="create-strategy-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Describe your strategy setup, rules, and constraints…"
            className="min-h-[120px] font-[family-name:var(--font-primary)]"
          />
        </CreateStrategyFieldBlock>
      </div>
    ) : (
      <div className="flex w-full min-w-0 flex-col gap-4">
        <TesseraFriendlyStructurePanel />
        <StrategyPineEditor
          key={formKey}
          value={pinescript}
          onChange={setPinescript}
          subtleText="var(--muted-foreground)"
          textColor="var(--foreground)"
          borderColor="var(--border)"
          minH="260px"
          brandFields
          textareaId="create-strategy-pinescript"
          onParseSessionChange={setPineSession}
        />
        <StrategyPineHelpPanel />
      </div>
    );

  const wizardBody = (
    <CreateStrategyWizardBody
      activeStepIndex={activeStepIndex}
      onActiveStepIndexChange={setActiveStepIndex}
      stepBody={stepBody}
      lastStepIndex={lastStepIndex}
      canAdvanceDetails={canAdvanceDetails}
      canCreate={canCreate}
      creating={creating}
      goBack={goBack}
      goNext={goNext}
      onCreate={() => void handleCreate()}
      onCancel={() => handleOpenChange(false)}
    />
  );

  return (
    <WorkspaceWizardDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={existingAttach ? "Add strategy" : "New strategy"}
      description={
        existingAttach
          ? "Create a new strategy or link one you already have to this account."
          : "Create a strategy for the active account. Add optional master Pinescript in a later step."
      }
      animateHeight
      pinFooterSlot
      className={cn(
        WORKSPACE_WIZARD_DIALOG_SIZE_CLASS,
        "dark:border-border dark:bg-sidebar dark:text-sidebar-foreground"
      )}
      bodyClassName="flex flex-col p-0"
    >
      {existingAttach ? (
        <>
          <div className="shrink-0 px-6 pt-4">
            <Tabs
              data-trade-log-tabs=""
              data-create-strategy-dialog-mode-tabs=""
              value={mode}
              onValueChange={(value) => {
                if (value === "create" || value === "existing") setMode(value);
              }}
              className="w-full"
            >
              <div className="w-full overflow-x-auto overflow-y-hidden">
                <TabsList className="min-w-max">
                  <TabsTrigger value="create">Create new</TabsTrigger>
                  <TabsTrigger value="existing">Add existing</TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </div>
          {mode === "create" ? (
            wizardBody
          ) : (
            <CreateStrategyExistingAttachBody
              brokerAccountCode={existingAttach.brokerAccountCode}
              excludeEntityIds={existingAttach.excludeEntityIds}
              strategyAlreadyTakenMessage={existingAttach.strategyAlreadyTakenMessage}
              onAttach={async (entityId) => {
                await existingAttach.onAttach(entityId);
                onOpenChange(false);
              }}
              onCancel={() => handleOpenChange(false)}
            />
          )}
        </>
      ) : (
        wizardBody
      )}
    </WorkspaceWizardDialog>
  );
}

function CreateStrategyExistingAttachBody({
  brokerAccountCode,
  excludeEntityIds,
  strategyAlreadyTakenMessage,
  onAttach,
  onCancel,
}: {
  brokerAccountCode: string;
  excludeEntityIds: ReadonlySet<string> | string[];
  strategyAlreadyTakenMessage?: string;
  onAttach: (entityId: string) => void | Promise<void>;
  onCancel: () => void;
}): React.ReactElement {
  const footerSlot = useWorkspaceWizardFooterSlot();

  const footer = (
    <DialogFooter
      data-create-strategy-existing-footer=""
      showCloseButton={false}
      className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 dark:border-border dark:bg-sidebar sm:flex-row sm:justify-end"
    >
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
        onClick={onCancel}
      >
        <span data-font="primary" className="truncate normal-case tracking-normal">
          Cancel
        </span>
      </Button>
    </DialogFooter>
  );

  return (
    <>
      {footerSlot ? createPortal(footer, footerSlot) : null}
      <div
        data-create-strategy-existing-attach=""
        className="flex w-full min-w-0 flex-col gap-4 px-6 py-4"
      >
        <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-muted-foreground dark:text-sidebar-muted-foreground">
          Choose a strategy that is not already on this account. Strategies from other
          accounts will be linked here.
        </p>
        <WorkspaceAttachExistingPicker
          active
          kind="strategy"
          brokerAccountCode={brokerAccountCode}
          excludeEntityIds={excludeEntityIds}
          onAttach={onAttach}
          strategyAlreadyTakenMessage={strategyAlreadyTakenMessage}
        />
      </div>
    </>
  );
}

function CreateStrategyWizardBody({
  activeStepIndex,
  onActiveStepIndexChange,
  stepBody,
  lastStepIndex,
  canAdvanceDetails,
  canCreate,
  creating,
  goBack,
  goNext,
  onCreate,
  onCancel,
}: {
  activeStepIndex: number;
  onActiveStepIndexChange: (index: number) => void;
  stepBody: React.ReactNode;
  lastStepIndex: number;
  canAdvanceDetails: boolean;
  canCreate: boolean;
  creating: boolean;
  goBack: () => void;
  goNext: () => void;
  onCreate: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const footerSlot = useWorkspaceWizardFooterSlot();

  const footer = (
    <DialogFooter
      data-create-strategy-wizard-footer=""
      showCloseButton={false}
      className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 dark:border-border dark:bg-sidebar sm:flex-row sm:justify-end"
    >
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
        onClick={activeStepIndex > 0 ? goBack : onCancel}
        disabled={creating}
      >
        <span data-font="primary" className="truncate normal-case tracking-normal">
          {activeStepIndex > 0 ? "Back" : "Cancel"}
        </span>
      </Button>
      {activeStepIndex < lastStepIndex ? (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={goNext}
          disabled={!canAdvanceDetails || creating}
        >
          <span className="truncate normal-case tracking-normal">Continue</span>
        </Button>
      ) : (
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={onCreate}
          disabled={!canCreate || creating}
        >
          {creating ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Creating…
            </>
          ) : (
            <span className="truncate normal-case tracking-normal">Create strategy</span>
          )}
        </Button>
      )}
    </DialogFooter>
  );

  return (
    <>
      {footerSlot ? createPortal(footer, footerSlot) : null}
      <div
        data-create-strategy-dialog=""
        data-create-strategy-wizard=""
        className="flex w-full flex-col px-6 py-4"
      >
        <GenerateReportWizardStepper
          activeStepIndex={activeStepIndex}
          onActiveStepIndexChange={onActiveStepIndexChange}
          stepCount={CREATE_STRATEGY_STEPS.length}
          compact
        >
          {CREATE_STRATEGY_STEPS.map((step, index) => (
            <GenerateReportWizardStepperContent key={step.key} step={index + 1}>
              <div className="mb-4">
                <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
                  {step.label}
                </p>
                <p className="mt-1 m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-muted-foreground dark:text-sidebar-muted-foreground">
                  {step.description}
                </p>
              </div>
              {stepBody}
            </GenerateReportWizardStepperContent>
          ))}
        </GenerateReportWizardStepper>
      </div>
    </>
  );
}
